import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound, CheckCircle } from 'lucide-react';
import { useAdminT } from '@/lib/adminTranslations';

const API_BASE = '';

export default function AdminChangePassword() {
  const t = useAdminT();
  const token = localStorage.getItem('admin_token');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (newPw.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { setError('Passwords do not match'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      const data = await res.json();
      if (data.status === 'password_changed') {
        setSuccess(true);
        setCurrentPw('');
        setNewPw('');
        setConfirmPw('');
      } else {
        setError(data.detail || 'Failed');
      }
    } catch {
      setError('Request failed');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-md" data-testid="admin-change-password">
      <h1 className="text-2xl font-bold text-slate-100 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
        <KeyRound className="w-6 h-6 inline mr-2 text-amber-400" />
        {t.changePassword || 'Change Password'}
      </h1>

      {success && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 mb-4 flex items-center gap-2" data-testid="password-success">
          <CheckCircle className="w-5 h-5 text-emerald-400" />
          <span className="text-sm text-emerald-400">Password changed successfully</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">{error}</div>
        )}
        <div>
          <Label className="text-slate-400 text-sm">Current Password</Label>
          <Input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)}
            className="mt-1 bg-slate-800 border-slate-700 text-slate-200" required data-testid="current-password" />
        </div>
        <div>
          <Label className="text-slate-400 text-sm">New Password</Label>
          <Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
            className="mt-1 bg-slate-800 border-slate-700 text-slate-200" required minLength={8}
            placeholder="Min 8 characters" data-testid="new-password-input" />
        </div>
        <div>
          <Label className="text-slate-400 text-sm">Confirm New Password</Label>
          <Input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
            className="mt-1 bg-slate-800 border-slate-700 text-slate-200" required data-testid="confirm-password-input" />
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="change-pw-submit">
          <KeyRound className="w-4 h-4 mr-2" />
          {loading ? 'Changing...' : (t.save || 'Change Password')}
        </Button>
      </form>
    </div>
  );
}
